import { useEffect, useMemo, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { GTabProps } from './types'

export const GTab = ({
	tabs,
	activeTab,
	defaultActiveKey,
	onTabChange,
	className
}: GTabProps): JSX.Element | null => {
	if (!tabs || tabs.length === 0) return null

	const isControlled = typeof activeTab !== 'undefined'

	const initialActiveKey = useMemo(() => {
		if (isControlled && activeTab) return activeTab
		if (defaultActiveKey) return defaultActiveKey
		return tabs[0].key
	}, [activeTab, defaultActiveKey, isControlled, tabs])

	const [internalActive, setInternalActive] = useState(initialActiveKey)

	useEffect(() => {
		if (isControlled && activeTab) {
			setInternalActive(activeTab)
		}
	}, [activeTab, isControlled])

	const currentActive = isControlled ? activeTab ?? internalActive : internalActive

	const handleTabClick = (key: string) => {
		if (!isControlled) {
			setInternalActive(key)
		}
		onTabChange?.(key)
	}

	return (
		<div
			className={twMerge(
				'flex items-center space-x-6 border-b border-gray-200',
				className
			)}
		>
			{tabs.map((tab) => (
				<button
					key={tab.key}
					onClick={() => handleTabClick(tab.key)}
					className={twMerge(
						'pb-3 px-1 text-lg font-medium flex items-center gap-2',
						currentActive === tab.key
							? 'border-b-2 border-t-email-blue text-t-google-ad'
							: 'text-t-secondary'
					)}
					aria-pressed={currentActive === tab.key}
				>
					{tab.label}
					{tab.badge}
				</button>
			))}
		</div>
	)
}
